import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, LayoutDashboard, Building, Lock, Mail, Eye, EyeOff } from 'lucide-react';
import axiosClient from '../api/axiosClient';

export default function SandboxLogin() {
  const [formData, setFormData] = useState({
    email: '',
    trialId: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const res = await axiosClient.post('/trials/sandbox-login', formData);
      localStorage.setItem('sandboxToken', res.data.token);
      if (res.data.redirectUrl) {
        window.location.href = res.data.redirectUrl;
      } else {
        navigate('/sandbox');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid credentials or Trial ID.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl overflow-hidden border border-slate-100">
        <div className="bg-primary-600 p-8 text-center">
          <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-4 text-white">
            <LayoutDashboard size={32} />
          </div>
          <h2 className="text-2xl font-display font-bold text-white">Trial Portal Login</h2>
          <p className="text-primary-100 mt-2 text-sm">Access your isolated ERP instance</p>
        </div>

        <form onSubmit={handleLogin} className="p-8 space-y-5">
          {error && (
            <div className="p-3 bg-red-50 text-red-600 text-sm rounded-lg border border-red-100">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email Address</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input required type="email" 
                className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none transition-shadow" 
                value={formData.email} 
                onChange={e => setFormData({...formData, email: e.target.value})} 
                placeholder="name@example.com" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Trial ID</label>
            <div className="relative">
              <Building className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input required type="text" 
                className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none transition-shadow" 
                value={formData.trialId} 
                onChange={e => setFormData({...formData, trialId: e.target.value})} 
                placeholder="e.g. trial_1234abcd" />
            </div>
            <p className="text-xs text-slate-500 mt-1">Check your approval email for this ID.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input required type={showPassword ? "text" : "password"} 
                className="w-full pl-10 pr-10 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none transition-shadow" 
                value={formData.password} 
                onChange={e => setFormData({...formData, password: e.target.value})} 
                placeholder="••••••••" />
              <button 
                type="button" 
                onClick={() => setShowPassword(!showPassword)} 
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 focus:outline-none"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <button disabled={loading} type="submit" 
            className="w-full bg-primary-600 text-white font-medium py-3 rounded-lg hover:bg-primary-700 transition-colors mt-2 disabled:opacity-70 flex justify-center items-center gap-2">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Access Sandbox'}
          </button>

          <div className="text-center mt-4">
            <button type="button" onClick={() => navigate('/')} className="text-sm text-slate-500 hover:text-slate-700 transition-colors">
              Return to Homepage
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
